export interface ContentItem {
  id: string;
  title: string;
  imageSrc: string;
  embedId?: string;
  videoSrc?: string;
}

export type ContentListMap = {
  [listName: string]: ContentItem[];
};

const STORAGE_KEY = 'minhasListas';
const LEGACY_KEY = 'minhaLista';
const DEFAULT_LIST = 'Meus Favoritos';

/**
 * Recupera todas as listas do localStorage.
 * Se necessário, migra dados antigos da chave "minhaLista" para "Meus Favoritos".
 */
export const getAllLists = (): ContentListMap => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    const parsed: ContentListMap = data ? JSON.parse(data) : {};

    if (!parsed[DEFAULT_LIST]) {
      const legacy = localStorage.getItem(LEGACY_KEY);
      parsed[DEFAULT_LIST] = legacy ? JSON.parse(legacy) : [];
    }

    return parsed;
  } catch (error) {
    console.error('Erro ao carregar listas:', error);
    return { [DEFAULT_LIST]: [] };
  }
};

/**
 * Salva todas as listas no localStorage e dispara evento de atualização.
 */
export const saveAllLists = (lists: ContentListMap) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lists));
    window.dispatchEvent(new Event('minhasListasUpdated'));
  } catch (error) {
    console.error('Erro ao salvar listas:', error);
  }
};

/**
 * Cria uma nova lista personalizada.
 */
export const createList = (name: string): boolean => {
  const lists = getAllLists();
  if (lists[name]) return false;

  lists[name] = [];
  saveAllLists(lists);
  return true;
};

/**
 * Renomeia uma lista existente (exceto a padrão).
 */
export const renameList = (oldName: string, newName: string): boolean => {
  if (oldName === DEFAULT_LIST) return false;

  const lists = getAllLists();
  if (!lists[oldName] || lists[newName]) return false;

  lists[newName] = lists[oldName];
  delete lists[oldName];
  saveAllLists(lists);
  return true;
};

/**
 * Exclui uma lista personalizada (exceto a padrão).
 */
export const deleteList = (name: string): boolean => {
  if (name === DEFAULT_LIST) return false;

  const lists = getAllLists();
  if (!lists[name]) return false;

  delete lists[name];
  saveAllLists(lists);
  return true;
};

/**
 * Adiciona item a uma lista nomeada.
 */
export const addToList = (listName: string, item: ContentItem): boolean => {
  try {
    const lists = getAllLists();
    const list = lists[listName] || [];

    if (list.some(i => i.id === item.id)) return false;

    lists[listName] = [...list, item];
    saveAllLists(lists);
    return true;
  } catch (error) {
    console.error('Erro ao adicionar item à lista:', error);
    return false;
  }
};

/**
 * Remove item de uma lista nomeada.
 */
export const removeFromList = (listName: string, itemId: string): boolean => {
  try {
    const lists = getAllLists();
    const list = lists[listName];
    if (!list) return false;

    lists[listName] = list.filter(i => i.id !== itemId);
    saveAllLists(lists);
    return true;
  } catch (error) {
    console.error('Erro ao remover item da lista:', error);
    return false;
  }
};

/**
 * Verifica se item está presente em uma lista nomeada.
 */
export const isInList = (listName: string, itemId: string): boolean => {
  try {
    const lists = getAllLists();
    const list = lists[listName];
    if (!list) return false;

    return list.some(i => i.id === itemId);
  } catch (error) {
    console.error('Erro ao verificar item na lista:', error);
    return false;
  }
};

/**
 * ✅ Compatibilidade: operações na lista antiga "minhaLista"
 * (mantidas para retrocompatibilidade com versões anteriores do projeto)
 */
export const addToMinhaLista = (item: ContentItem): boolean => {
  return addToList(DEFAULT_LIST, item);
};

export const removeFromMinhaLista = (itemId: string): boolean => {
  return removeFromList(DEFAULT_LIST, itemId);
};

export const isInMinhaLista = (itemId: string): boolean => {
  return isInList(DEFAULT_LIST, itemId);
};

export const getAvailableLists = (): string[] => {
  const lists = getAllLists();
  return Object.keys(lists);
};
